import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useForm } from 'react-hook-form';
import Grid from '@mui/material/Grid';
import { FormInput } from 'components/molecules/FormInput';
import { initialRefereeDetailsStepFormValues } from 'models/forms/applicationForm';
import { yupResolver } from '@hookform/resolvers/yup';
import ApplicationStore from 'state/ApplicationStore';
import { EDictionaryTypes } from 'models/dictionary';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { FormSelect } from 'components/molecules/FormSelect';
import { FormDatePicker } from 'components/molecules/FormDatePicker';
import { FormRadioGroup } from 'components/molecules/FormRadioGroup';
import { useApplyWizard } from 'pages/ApplyOnlinePage/ApplyOnlineWizardPage/hooks';
import {
  StyledParagraph,
  StyledSectionTitle,
  StyledStepTitle,
} from 'pages/ApplyOnlinePage/ApplyOnlineWizardPage/WizardSteps/styles';
import { useRefereeDetailsStepFormSchema } from './schemaValidations';

interface IRefereeDetailsStep {
  setNextStep: (arg: number) => void;
}

const RefereeDetailsStep = ({ setNextStep }: IRefereeDetailsStep) => {
  const { schema } = useRefereeDetailsStepFormSchema();
  const { loadDictionaries, civilianPrefix } = useApplyWizard();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: initialRefereeDetailsStepFormValues,
    resolver: yupResolver(schema) as any,
  });

  const formRef = useRef<HTMLFormElement | null>(null);

  useEffect(() => {
    const handler = () => {
      if (formRef.current) {
        formRef.current.requestSubmit();
      }
    };
    document.addEventListener('submitFormEvent', handler);
    return () => {
      document.removeEventListener('submitFormEvent', handler);
    };
  }, []);

  useEffect(() => {
    (async function initDictionaries() {
      await loadDictionaries([EDictionaryTypes.civilianPrefix]);
    })();
  }, []);

  (window as any).fireCycleStepFour = () => {
    reset({
      referee1: {
        refereeTitle: 'Mr.',
        refereeSurname: 'John',
        refereeForenames: 'Doe',
        refereePreviousNames: 'Doe',
        refereeBirthDate: '1999-04-10',
        refereeHomeAddress: 'Green Street 11',
        refereeOccupation: 'ABC Company',
        refereeWorkMobileNumber: '',
        refereeWorkEmail: '',
        refereeCapacity: 'No answer.',
        refereeKnown: '10 years',
      },
      referee2: {
        refereeTitle: 'Mr.',
        refereeSurname: 'John',
        refereeForenames: 'Doe',
        refereePreviousNames: 'Doe',
        refereeBirthDate: '1999-04-10',
        refereeHomeAddress: 'Green Street 11',
        refereeOccupation: 'ABC Company',
        refereeWorkMobileNumber: '',
        refereeWorkEmail: '',
        refereeCapacity: 'No answer.',
        refereeKnown: '10 years',
      },
    });
  };

  useEffect(() => {
    const formData = ApplicationStore.getStepData('refereeData');
    if (Object.keys(formData).length > 0) {
      reset(formData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    ApplicationStore.setStepErrors(3, Object.keys(errors).length > 0);
  }, [errors]);

  const onSubmitForm = (data: any) => {
    ApplicationStore.setStepData('refereeData', data);
    ApplicationStore.setStepErrors(3, false);
    setNextStep(4);
  };

  const refereeForm = (number: string) => (
    <Grid container rowGap={1}>
      <StyledSectionTitle variant="h5">{`Referee ${number}`}</StyledSectionTitle>
      <Grid container spacing={1}>
        <Grid item xs={4}>
          <FormSelect
            options={mapDictionaryToOptionProp(civilianPrefix)}
            setValue={setValue}
            name={`referee${number}.refereeTitle`}
            label="Title"
            required
            control={control}
            errors={errors}
          />
        </Grid>
        <Grid item xs={4}>
          <FormInput
            name={`referee${number}.refereeSurname`}
            label="Surname"
            required
            control={control}
            errors={errors}
          />
        </Grid>
        <Grid item xs={4}>
          <FormInput
            name={`referee${number}.refereeForenames`}
            label="Forenames"
            required
            control={control}
            errors={errors}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        <Grid item xs={4}>
          <FormInput
            name={`referee${number}.refereePreviousNames`}
            label="Previous Names"
            control={control}
            errors={errors}
          />
        </Grid>
        <Grid item xs={4}>
          <FormDatePicker
            name={`referee${number}.refereeBirthDate`}
            openTo="year"
            label="Birth Date"
            control={control}
            errors={errors}
            required
          />
        </Grid>
        <Grid item xs={4}>
          <FormInput
            name={`referee${number}.refereeBirthPlace`}
            label="Birth Place"
            control={control}
            errors={errors}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        <Grid item xs={8}>
          <FormInput
            name={`referee${number}.refereeHomeAddress`}
            label="Home Address"
            required
            control={control}
            errors={errors}
          />
        </Grid>
        <Grid item xs={4}>
          <FormInput
            name={`referee${number}.refereeHomePostcode`}
            label="Home Postcode"
            control={control}
            errors={errors}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        <Grid item xs={4}>
          <FormInput
            name={`referee${number}.refereeHomeMobileNumber`}
            label="Home Mobile Number"
            control={control}
            errors={errors}
          />
        </Grid>
        <Grid item xs={4}>
          <FormInput
            name={`referee${number}.refereeHomeEmail`}
            label="Home E-mail"
            control={control}
            errors={errors}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <FormInput
            name={`referee${number}.refereeOccupation`}
            label="Occupation"
            required
            control={control}
            errors={errors}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        <Grid item xs={4}>
          <FormInput
            name={`referee${number}.refereeWorkMobileNumber`}
            label="Work Mobile Number"
            control={control}
            errors={errors}
          />
        </Grid>
        <Grid item xs={4}>
          <FormInput
            name={`referee${number}.refereeWorkEmail`}
            label="Work Email"
            control={control}
            errors={errors}
          />
        </Grid>
      </Grid>

      <Grid container spacing={1}>
        <Grid item xs={12}>
          <FormInput
            name={`referee${number}.refereeCapacity`}
            label="In what capacity do you know the referee?"
            required
            control={control}
            errors={errors}
          />
        </Grid>
      </Grid>

      <Grid container spacing={1}>
        <Grid item xs={12}>
          <FormInput
            name={`referee${number}.refereeKnown`}
            label="How long has the referee known you?"
            required
            control={control}
            errors={errors}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <FormRadioGroup
            name={`referee${number}.refereeGuidanceInformation`}
            label="Have you provided the referee with the guidance information for this application?"
            options={[
              { label: 'YES', value: '1' },
              { label: 'NO', value: '0' },
            ]}
            control={control}
            errors={errors}
          />
        </Grid>
      </Grid>
    </Grid>
  );

  return (
    <Box>
      <StyledStepTitle variant="h4">Referee Details</StyledStepTitle>
      <StyledParagraph variant="body2">
        Please give details of a suitable person who has agreed to act as a referee for you.
      </StyledParagraph>

      <form onSubmit={handleSubmit(onSubmitForm)} ref={formRef}>
        {refereeForm('1')}
        {refereeForm('2')}
      </form>
    </Box>
  );
};

export default RefereeDetailsStep;
